import { useEffect, useRef, useState } from 'react';

/**
 * Polling utilities to align the UI with the backend's eventually consistent read models.
 * The backend uses projectors that update read models asynchronously after commands.
 * After a create/update/delete, list and find APIs can be briefly stale. These helpers
 * wait until the read model reflects the change (resource appears, disappears, or list
 * satisfies a condition) before treating the mutation as complete.
 *
 * Running out of attempts is NOT a failure. The command already succeeded before
 * polling began; only the read model is behind. These helpers resolve (with null,
 * or void where there is nothing to return) so callers keep the id they already
 * hold instead of treating a lagging projection as a failed mutation, which makes
 * the user retry and create a duplicate.
 *
 * If the read model is broken (e.g. repeated 5xx or network errors), polling stops
 * after maxConsecutiveErrors and throws ReadModelUnavailableError so the UI can show
 * a "please refresh" message instead of burning all attempts.
 *
 * Polling always starts with a short initial delay (EVENTUAL_CONSISTENCY_POLL_INITIAL_DELAY_MS)
 * before the first attempt to avoid immediate failure when the read model updates very quickly.
 */

/** Default max attempts for eventual-consistency polling. */
export const EVENTUAL_CONSISTENCY_POLL_MAX_ATTEMPTS = 10;

/** Default delay in ms between polling attempts. */
export const EVENTUAL_CONSISTENCY_POLL_DELAY_MS = 300;

/** Default consecutive errors before we stop polling (read model likely broken). */
export const EVENTUAL_CONSISTENCY_POLL_MAX_CONSECUTIVE_ERRORS = 3;

/** Delay in ms before the first polling attempt. Avoids immediate failure when the read model updates very quickly. */
export const EVENTUAL_CONSISTENCY_POLL_INITIAL_DELAY_MS = 5;

/**
 * Thrown when polling stops early because the read model is likely broken
 * (repeated errors from the fetch/check function). UI can catch this and
 * show e.g. "Unable to verify changes. Please refresh the page."
 */
export class ReadModelUnavailableError extends Error {
  readonly name = 'ReadModelUnavailableError';
  constructor(
    message: string = 'Read model unavailable; please refresh the page.',
    public readonly cause?: unknown
  ) {
    super(message);
  }
}

export type PollOptions = {
  maxAttempts?: number;
  delayMs?: number;
  /** Stop polling after this many consecutive errors (default: 3). Prevents hammering a broken read model. */
  maxConsecutiveErrors?: number;
};

function getPollOptions(options?: PollOptions) {
  return {
    maxAttempts: options?.maxAttempts ?? EVENTUAL_CONSISTENCY_POLL_MAX_ATTEMPTS,
    delayMs: options?.delayMs ?? EVENTUAL_CONSISTENCY_POLL_DELAY_MS,
    maxConsecutiveErrors:
      options?.maxConsecutiveErrors ??
      EVENTUAL_CONSISTENCY_POLL_MAX_CONSECUTIVE_ERRORS,
  };
}

/**
 * Poll for a resource to become available.
 * Attempts to fetch a resource multiple times with a delay between attempts.
 *
 * @param fetchFn - Async function that attempts to fetch the resource. Should return the resource or null/undefined if not found.
 * @param options - Polling configuration (defaults: EVENTUAL_CONSISTENCY_POLL_*)
 * @returns The fetched resource, or null if the read model had not caught up in time
 * @throws ReadModelUnavailableError if the fetch keeps failing (read model likely broken)
 *
 * @example
 * const team = await pollForResource(
 *   () => teamService.findTeam(tenantId, teamId),
 *   { maxAttempts: 10, delayMs: 300 }
 * );
 */
export async function pollForResource<T>(
  fetchFn: () => Promise<T | null | undefined>,
  options?: PollOptions
): Promise<T | null> {
  const { maxAttempts, delayMs, maxConsecutiveErrors } =
    getPollOptions(options);
  let consecutiveErrors = 0;

  await new Promise((resolve) =>
    setTimeout(resolve, EVENTUAL_CONSISTENCY_POLL_INITIAL_DELAY_MS)
  );

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await fetchFn();
      consecutiveErrors = 0;
      if (result) {
        return result;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new ReadModelUnavailableError(
          'Unable to verify the change; the service may be temporarily unavailable. Please refresh the page.',
          err
        );
      }
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  // Out of attempts: the resource exists, the read model is just behind.
  return null;
}

/**
 * Poll for a resource to no longer exist (e.g. after delete/remove).
 * Succeeds when fetchFn returns null or undefined (resource gone from read model).
 *
 * @param fetchFn - Async function that fetches the resource. Should return null/undefined when the resource is gone.
 * @param options - Polling configuration (defaults: EVENTUAL_CONSISTENCY_POLL_*)
 * @throws ReadModelUnavailableError if the fetch keeps failing (read model likely broken)
 *
 * @example
 * await pollForResourceGone(() => secretService.findSecret(tenantId, secretId));
 */
export async function pollForResourceGone(
  fetchFn: () => Promise<unknown>,
  options?: PollOptions
): Promise<void> {
  const { maxAttempts, delayMs, maxConsecutiveErrors } =
    getPollOptions(options);
  let consecutiveErrors = 0;

  await new Promise((resolve) =>
    setTimeout(resolve, EVENTUAL_CONSISTENCY_POLL_INITIAL_DELAY_MS)
  );

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const result = await fetchFn();
      consecutiveErrors = 0;
      if (result == null) {
        return;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new ReadModelUnavailableError(
          'Unable to verify the change; the service may be temporarily unavailable. Please refresh the page.',
          err
        );
      }
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  // Out of attempts: the delete succeeded, the read model is just behind.
}

/**
 * Poll until a condition is satisfied (e.g. list reflects a grant/revoke).
 * Succeeds when checkFn returns true.
 *
 * @param checkFn - Async function that returns true when the read model reflects the change.
 * @param options - Polling configuration (defaults: EVENTUAL_CONSISTENCY_POLL_*)
 * @throws ReadModelUnavailableError if the check keeps failing (read model likely broken)
 *
 * @example
 * await pollUntilCondition(
 *   async () => {
 *     const list = await roleService.listRolePermissions(tenantId, roleId);
 *     return list.some((p) => p.name === permission);
 *   },
 *   { maxAttempts: 10, delayMs: 300 }
 * );
 */
export async function pollUntilCondition(
  checkFn: () => Promise<boolean>,
  options?: PollOptions
): Promise<void> {
  const { maxAttempts, delayMs, maxConsecutiveErrors } =
    getPollOptions(options);
  let consecutiveErrors = 0;

  await new Promise((resolve) =>
    setTimeout(resolve, EVENTUAL_CONSISTENCY_POLL_INITIAL_DELAY_MS)
  );

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      const ok = await checkFn();
      consecutiveErrors = 0;
      if (ok) {
        return;
      }
    } catch (err) {
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw new ReadModelUnavailableError(
          'Unable to verify the change; the service may be temporarily unavailable. Please refresh the page.',
          err
        );
      }
    }
    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  // Out of attempts: the command succeeded, the read model is just behind.
}

/**
 * Poll until a fetched resource has all expected field values (e.g. after an update).
 * Use this after update mutations so the UI only proceeds when the read model
 * reflects every field you changed, without manually comparing each property.
 *
 * @param fetchFn - Async function that fetches the resource (e.g. find by id).
 * @param expected - Partial object; only these keys are checked. Resource must match all. Use primitive or reference-comparable values (===); avoid relying on deep equality for objects/arrays.
 * @param options - Polling configuration (defaults: EVENTUAL_CONSISTENCY_POLL_*)
 * @returns The fetched resource once it matches expected, or null if the read model had not caught up in time
 * @throws ReadModelUnavailableError if the fetch keeps failing (read model likely broken)
 *
 * @example
 * // After updating a role's name and description:
 * const role = await pollUntilResourceMatches(
 *   () => roleService.findRole(tenantId, roleId),
 *   { name: 'New Name', description: 'New desc' },
 *   { maxAttempts: 10, delayMs: 300 }
 * );
 */
export async function pollUntilResourceMatches<T extends object>(
  fetchFn: () => Promise<T | null | undefined>,
  expected: Partial<T>,
  options?: PollOptions
): Promise<T | null> {
  const keys = Object.keys(expected) as (keyof T)[];
  if (keys.length === 0) {
    return pollForResource(fetchFn, options);
  }
  return pollForResource(async () => {
    const resource = await fetchFn();
    if (resource == null) return null;
    for (const key of keys) {
      if (resource[key] !== (expected as T)[key]) return null;
    }
    return resource;
  }, options);
}

/** Options for version-based polling (backend stamps read models with e.g. _rev). */
export type PollUntilNewerOptions = PollOptions & {
  /** Read version from the resource (default: r._rev ?? -1). Use when the backend uses a different field (e.g. etag, updated_at). */
  getVersion?: (resource: unknown) => number;
};

/**
 * Poll until the fetched resource has a version greater than minVersion.
 * Use when the backend stamps read models with a meta version field (e.g. _rev); after an update,
 * you only need to wait until the read model returns a newer version—no per-field comparison.
 *
 * @param fetchFn - Async function that fetches the resource (e.g. find by id).
 * @param minVersion - Version the resource must exceed (e.g. the version you had before the update). Use -1 when no version is available yet (then we accept any resource once it exists).
 * @param options - Polling config and optional getVersion (default: read _rev from resource).
 * @returns The fetched resource once its version > minVersion, or null if the read model had not caught up in time.
 * @throws ReadModelUnavailableError if the fetch keeps failing (read model likely broken)
 *
 * @example
 * // Once backend adds _rev to role responses:
 * const currentVersion = cachedRole?._rev ?? -1;
 * await roleService.updateRole(tenantId, roleId, updates);
 * const updated = await pollUntilResourceNewerThan(
 *   () => roleService.findRole(tenantId, roleId),
 *   currentVersion,
 *   { maxAttempts: 10, delayMs: 300 }
 * );
 */
export async function pollUntilResourceNewerThan<T>(
  fetchFn: () => Promise<T | null | undefined>,
  minVersion: number,
  options?: PollUntilNewerOptions
): Promise<T | null> {
  const getVersion =
    options?.getVersion ??
    ((r: unknown) => (r as { _rev?: number })._rev ?? -1);
  const { getVersion: _g, ...pollOpts } = options ?? {};

  return pollForResource(async () => {
    const resource = await fetchFn();
    if (resource == null) return null;
    if (getVersion(resource) > minVersion) return resource;
    return null;
  }, pollOpts);
}

// ---------------------------------------------------------------------------
// React polling hooks
// ---------------------------------------------------------------------------

/**
 * Suppresses a "no data" state for a short window after `trigger` changes.
 * Useful when the read model can return a transient 404 immediately after an
 * action (e.g. invoking a run) before the projection catches up.
 *
 * Returns `true` for `durationMs` after every change to `trigger` (when
 * `trigger` is not `undefined`), then switches back to `false`.
 *
 * @param trigger - Any value; the timer resets whenever this changes.
 * @param durationMs - How long the grace period lasts in ms.
 *
 * @example
 * const isGracePeriod = useGracePeriod(runSequence, 3000);
 * // isLoading = query.isLoading || (isGracePeriod && !data)
 */
export function useGracePeriod(trigger: unknown, durationMs: number): boolean {
  const [isGracePeriod, setIsGracePeriod] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (trigger === undefined) {
      setIsGracePeriod(false);
      return;
    }
    setIsGracePeriod(true);
    timerRef.current = window.setTimeout(() => {
      setIsGracePeriod(false);
      timerRef.current = null;
    }, durationMs);
  }, [trigger, durationMs]);

  return isGracePeriod;
}

/**
 * Options for ramping up the delay between polling calls.
 * Use for long-running operations that may take minutes or hours.
 */
export type RampOptions = {
  /**
   * Multiplier applied to the current delay after each poll tick.
   * Must be > 1. Defaults to `1.5`.
   */
  factor?: number;
  /**
   * Upper bound on the delay in ms. The delay will not grow beyond this value.
   * Defaults to `15_000` (15 seconds).
   */
  maxDelayMs?: number;
};

/**
 * Fires `refetch()` on a timer whenever `shouldPoll` is true.
 *
 * Without `ramp`, the delay between calls is fixed at `delayMs`.
 *
 * With `ramp`, the delay starts at `delayMs` and multiplies by `ramp.factor`
 * (default `1.5`) after each tick, capped at `ramp.maxDelayMs` (default
 * `15_000` ms). The delay resets to `delayMs` whenever `shouldPoll` becomes
 * `false`, so a new run always starts polling at the fast rate.
 *
 * @param shouldPoll - Whether polling is currently active.
 * @param refetch - Callback to fire on each tick.
 * @param delayMs - Initial (and fixed, when no ramp) delay in ms.
 * @param ramp - Optional ramp config for long-running operations.
 * @param resetKey - When this value changes, the ramp resets back to `delayMs`.
 *   Useful for restarting the ramp at the beginning of each status phase.
 *
 * @example
 * // Fixed interval (short-lived runs)
 * usePollingRefetch(shouldPoll, () => void query.refetch(), 1000);
 *
 * // Ramping interval (long-running jobs, up to 15 s)
 * usePollingRefetch(shouldPoll, () => void query.refetch(), 1000, { factor: 1.5, maxDelayMs: 15_000 });
 *
 * // Ramp that restarts whenever status changes
 * usePollingRefetch(shouldPoll, () => void query.refetch(), 1000, { factor: 1.5, maxDelayMs: 15_000 }, status);
 */
export function usePollingRefetch(
  shouldPoll: boolean,
  refetch: () => unknown,
  delayMs: number,
  ramp?: RampOptions,
  resetKey?: unknown
): void {
  const currentDelayRef = useRef(delayMs);

  // Reset the ramp whenever polling stops or the resetKey changes.
  useEffect(() => {
    currentDelayRef.current = delayMs;
  }, [delayMs, resetKey]);

  useEffect(() => {
    if (!shouldPoll) {
      currentDelayRef.current = delayMs;
    }
  }, [shouldPoll, delayMs]);

  useEffect(() => {
    if (!shouldPoll) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (ramp) {
        const factor = ramp.factor ?? 1.5;
        const maxDelayMs = ramp.maxDelayMs ?? 15_000;
        currentDelayRef.current = Math.min(
          currentDelayRef.current * factor,
          maxDelayMs
        );
      }
      refetch();
    }, currentDelayRef.current);

    return () => {
      window.clearTimeout(timer);
    };
  }, [shouldPoll, refetch, ramp]);
}
